<script setup>
import Spinner from '@/images/Spinner.vue'
import { CheckIcon, XIcon } from "@heroicons/vue/outline"
import { ref, computed } from 'vue'

    
const props = defineProps({
    showDefaultText: {
        type: Boolean,
        default: true
    },
    loading: {
        type: Boolean,
        default: false
    },
    debounce: {
        type: Number,
        default: 0
    },
    status: {
        type: String,
    },
    color: {
        type: String,
        default: 'blue'
    },
	variant: {
		type: String,
		default: 'solid',
		validator: (value) => {
			return ['solid', 'outline', 'link'].includes(value)
		}
	},
})

const isLoading = ref(props.loading)
const showingDefaultText = ref(props.showDefaultText)
const loadingError = ref(false)
const loadingSuccess = ref(false)

// used to debounce our loading spinner (see waitFor)
function sleep(time) {
    return new Promise((resolve) => {
        setTimeout(resolve, time);
    });
}

//https://vuejs.org/guide/essentials/template-refs.html#ref-on-component
defineExpose({
    waitFor: async function(promise, inDebounce = 0){
        this.setLoading(true)
        try {
            const success = await promise // if the promise rejects we catch it below
            this.loadingSucceeded()

            // sometimes we want the tick to stay around before the promise resolves
            // like in situations where the UI is about to slide out of view we want
            // the user to see the tick for a split second more before it disappears
            await sleep(inDebounce || props.debounce)
        }
        catch (failure) {
            this.loadingFailed()
            throw (failure)
        }
    },
    setLoading(status){
        showingDefaultText.value = !status
        isLoading.value = status
    },
    loadingSucceeded(){
        showingDefaultText.value = false
        isLoading.value = false
        loadingError.value = false
        loadingSuccess.value = true
        window.setTimeout(() => {
            loadingSuccess.value = false
            showingDefaultText.value = true
        }, 1500)
    },
    loadingFailed(){
        showingDefaultText.value = false
        isLoading.value = false
        loadingSuccess.value = false
        loadingError.value = true
        window.setTimeout(() => {
            loadingError.value = false
            showingDefaultText.value = true
        }, 1500)
    }
})

// depending on the colour, our loading spinner may need to be white
const spinnerColor = computed(function() {
	if(props.variant == 'outline' || props.variant == 'link') {
		return 'fill-' + props.color
	}

	// else
    return 'fill-white';
});

</script>

<template>
    <!-- 
        The promise-button exposes some asynchronous cpabilities with user feedback.
        You'll want to use it something like this:
        
                <promise-button 
                    type="button" 
                    class="btn-shadow d-inline-flex align-items-center btn btn-success" 
                    @click="promiseButtonClicked"
                    ref="promiseBtn"
                    >
                        <font-awesome-icon class="mr-2" icon="plus"/>
                        Create New
                </promise-button>

        PLEASE NOTE: You will need to use the 'ref' property on the component in order to use
        the waitFor() function, as the 'ref' links into the vue vm, and the functions therein

        You can pass a promise to the button in your vue methods by listening to the buttons
        @click assigned function like so:
        
            promiseButtonClicked(){
                var button = this.$refs.promiseBtn
                button.waitFor(Promise)
                    .then(response => {
                        doYourThang()
                    )
            }

        waitFor() returns a promise, and also internally manages its own promise feedback.
        When you use the buttons waitFor() function, the button will automatically take care
        of showing a loading spinner and then a little tick or cross when the promise returns.
        You'll also want to hook into the Promise returned by the waitFor() function in order
        to do your own handling outside the button.
    -->

    <Button :color="color" :status="status" >
        <slot 
			v-if="showingDefaultText"
		>
		</slot>
        <Spinner 
			v-if="isLoading"
			size="sm" 
			:class="spinnerColor" 
		/>
        <XIcon 
			class="w-4 h-4"
			:class="spinnerColor"
			v-if="loadingError"
		/>
        <CheckIcon class="w-4 h-4" :class="spinnerColor" v-if="loadingSuccess" />
    </Button>

</template>
